import {
  isPdlMissingValue,
  resolveNavLogoSurface,
  retargetPlatformNavLogoPathForSurface,
} from '@web-studio/shared-schemas';
import { EditableContent } from '../../edit/EditableContent';
import { EditableMedia } from '../../edit/EditableMedia';
import { buildSiteImageProxyUrl, buildSiteImageSrcSet } from '../../lib/imageProxy';
import { SITE_IMAGE_SIZES } from '../../lib/responsiveImageSizes';
import { pickBlockField } from '../../lib/localized';
import { pickMediaUrl } from '../../lib/media';
import { parseHoursSchedule } from '../../lib/splitHoursLines';
import type { BlockRenderProps } from '../../types';
import { FooterLegalLinks } from './FooterLegalLinks';
import { FooterSocialLinks } from './FooterSocialLinks';

function pickFooterContactField(
  content: BlockRenderProps['block']['content'],
  key: string,
  locale: string,
): string {
  const value = pickBlockField(content, key, locale);
  return isPdlMissingValue(value) ? '' : value;
}

/** Sparse editorial masthead — oversized brand, social row, hairline legal. */
export function FooterColophon({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
  pages,
  defaultLocale = locale,
  themeId,
  colorsetId,
  onNavigateToPageSlug,
}: BlockRenderProps) {
  const siteTitle = pickBlockField(block.content, 'siteTitle', locale);
  const address = pickFooterContactField(block.content, 'address', locale);
  const phone = pickFooterContactField(block.content, 'phone', locale);
  const email = pickFooterContactField(block.content, 'email', locale);
  const hours = pickFooterContactField(block.content, 'hours', locale);
  const hoursRows = parseHoursSchedule(hours);
  const legalLine = pickBlockField(block.content, 'legalLine', locale);
  const target = isActive ? activeEditTarget : null;
  const logoImageRaw = pickMediaUrl(block, 'logoImage');
  const logoSurface = resolveNavLogoSurface(themeId, null, colorsetId);
  const logoImage = logoImageRaw
    ? retargetPlatformNavLogoPathForSurface(logoImageRaw, logoSurface)
    : undefined;
  const logoSrc = logoImage ? buildSiteImageProxyUrl(logoImage, 240, 78) : undefined;
  const logoSrcSet = logoImage
    ? buildSiteImageSrcSet(logoImage, [120, 180, 240], 78)
    : undefined;

  return (
    <footer
      className={['ws-footer-colophon', isActive ? 'ws-block--active' : ''].filter(Boolean).join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-footer-arrangement="colophon"
      id="contact"
    >
      <div className="ws-footer-colophon__inner">
        {logoSrc || siteTitle || editable ? (
          <div className="ws-footer-colophon__identity">
            {logoSrc ? (
              <div className="ws-footer-colophon__mark">
                <EditableMedia
                  fieldKey="logoImage"
                  editable={editable}
                  activeEditTarget={target}
                  className="ws-footer-colophon__mark-img"
                  src={logoSrc}
                  srcSet={logoSrcSet}
                  sizes={SITE_IMAGE_SIZES.heroMonolithSeal}
                  alt={siteTitle || 'Mark'}
                  width={120}
                  height={120}
                  loading="lazy"
                  decoding="async"
                />
              </div>
            ) : null}
            {siteTitle || editable ? (
              <EditableContent
                as="p"
                fieldKey="siteTitle"
                editable={editable}
                activeEditTarget={target}
                className="ws-footer-colophon__brand"
              >
                {siteTitle}
              </EditableContent>
            ) : null}
          </div>
        ) : null}
        <EditableContent
          as="p"
          fieldKey="address"
          editable={editable}
          activeEditTarget={target}
          className="ws-footer-colophon__address"
        >
          {address}
        </EditableContent>
        <div className="ws-footer-colophon__meta">
          <EditableContent
            as="p"
            fieldKey="phone"
            editable={editable}
            activeEditTarget={target}
            className="ws-footer-colophon__meta-item"
          >
            {phone ? <a href={`tel:${phone.replace(/\s/g, '')}`}>{phone}</a> : null}
          </EditableContent>
          <EditableContent
            as="p"
            fieldKey="email"
            editable={editable}
            activeEditTarget={target}
            className="ws-footer-colophon__meta-item"
          >
            {email ? <a href={`mailto:${email}`}>{email}</a> : null}
          </EditableContent>
        </div>
        {hoursRows.length > 0 ? (
          <EditableContent
            as="div"
            fieldKey="hours"
            editable={editable}
            activeEditTarget={target}
            className="ws-footer-colophon__hours ws-hours-schedule"
          >
            {hoursRows.map((row, index) => (
              <div key={index} className="ws-hours-schedule__row">
                <span className="ws-hours-schedule__day">{row.day}</span>
                <span className="ws-hours-schedule__time">{row.time}</span>
              </div>
            ))}
          </EditableContent>
        ) : null}
        <FooterSocialLinks
          content={block.content}
          locale={locale}
          editable={editable}
          activeEditTarget={target}
          className="ws-footer-colophon__social"
        />
        <div className="ws-footer-colophon__legal">
          <FooterLegalLinks
            pages={pages}
            locale={locale}
            defaultLocale={defaultLocale}
            editable={editable}
            activeEditTarget={target}
            content={block.content}
            onNavigateToPageSlug={onNavigateToPageSlug}
          />
          {legalLine || editable ? (
            <EditableContent
              fieldKey="legalLine"
              editable={editable}
              activeEditTarget={target}
              className="ws-footer__legal-line"
            >
              {legalLine}
            </EditableContent>
          ) : null}
        </div>
      </div>
    </footer>
  );
}
